import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArqueoPayment, formatYmd, isPaymentOnDate } from "@/lib/arqueo/aggregations";

interface Params {
  gymId: string | null;
  date: Date;
}

export const useDailyPayments = ({ gymId, date }: Params) => {
  const ymd = formatYmd(date);

  return useQuery({
    queryKey: ["arqueo-payments", gymId, ymd],
    queryFn: async (): Promise<ArqueoPayment[]> => {
      if (!gymId) return [];

      const [profilesRes, pendingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, username, first_name, last_name")
          .eq("gym_id", gymId),
        supabase
          .from("pending_members")
          .select("id, first_name, last_name, dni")
          .eq("gym_id", gymId),
      ]);

      const profilesList = profilesRes.data || [];
      const pendingList = (pendingRes.data || []).filter(
        (p: any) => p.dni !== "__WALKIN__"
      );
      const walkinIds = new Set(
        (pendingRes.data || [])
          .filter((p: any) => p.dni === "__WALKIN__")
          .map((p: any) => p.id as string)
      );

      const allUserIds = [
        ...profilesList.map((p) => p.id),
        ...pendingList.map((p) => p.id),
        ...Array.from(walkinIds),
      ];

      if (allUserIds.length === 0) return [];

      // Build a generous date window: paid_at is often stored at 00:00 UTC,
      // so we widen by 1 day on each side and then filter precisely client-side.
      const start = new Date(date);
      start.setDate(start.getDate() - 1);
      const end = new Date(date);
      end.setDate(end.getDate() + 2);
      const startIso = start.toISOString();
      const endIso = end.toISOString();

      const allPayments: any[] = [];
      const batchSize = 500;
      for (let i = 0; i < allUserIds.length; i += batchSize) {
        const batch = allUserIds.slice(i, i + batchSize);
        const { data, error } = await supabase
          .from("payments")
          .select("*")
          .in("user_id", batch)
          .gte("paid_at", startIso)
          .lte("paid_at", endIso)
          .is("parent_payment_id", null)
          .order("paid_at", { ascending: false });
        if (error) throw error;
        if (data) allPayments.push(...data);
      }

      const profilesMap = new Map(profilesList.map((p) => [p.id, p]));
      const pendingMap = new Map(pendingList.map((p) => [p.id, p]));

      const enriched: ArqueoPayment[] = allPayments.map((payment) => {
        const prof = profilesMap.get(payment.user_id);
        const pending = pendingMap.get(payment.user_id);
        const isWalkin = walkinIds.has(payment.user_id);
        return {
          ...payment,
          profiles: isWalkin
            ? null
            : prof || (pending
                ? {
                    username: null,
                    first_name: pending.first_name,
                    last_name: pending.last_name,
                  }
                : null),
        };
      });

      // Precise client-side filter by local-day match
      return enriched.filter((p) => isPaymentOnDate(p, ymd));
    },
    enabled: !!gymId,
  });
};
